import {
  FeedbackOrigin,
  FeedbackMenu,
  FeedbackBadge,
  ReportAiDisclosure,
} from '../../components/ai/DisclosureControls';
import { getFeedbackPreference } from '../../components/ai/aiDisclosure';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {Button} from '@betareader/design-system';
import AppIcon from '../../components/AppIcon';
import moment from 'moment';
import useElementSize from '../../hooks/useElementSize';
import {useDispatch, useSelector} from '../../reduxHooks';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import {useFeatureFlags} from '../../hooks/useFeatureFlags';
import FeedbackReportModal, {FeedbackReportTarget} from './feedbackReportModal';
import queryString from 'query-string';
import debounce from 'lodash/debounce';

import Avatar from '../../components/avatar/avatar';
import { getReaction } from '../../utils/reactionUtil';
import accessUtil from '../../utils/accessUtil';
import authUtil, { getFreshIdToken } from '../../utils/authUtil';

import {
  setActiveInlineComment,
  submitComment,
  setFilteredReaders,
  updateComment,
  editComment,
  editReply,
  updateReply,
  deleteReply,
  editCommentPlaceHolderText,
  removeInlineCommentPlaceholder,
  deleteComment,
  submitReply,
} from '../../modules/comment';

import TextAreaWithSave from '../../components/inputs/TextAreaWithSave';
import './commentElement.css';

const WordCount = styled.div<{ warning: boolean }>`
  font-size: 0.8em;
  font-style: italic;
  display: flex;
  width: 100%;
  justify-content: flex-end;
  ${({ theme, warning }) => css`
    color: ${warning ? theme.font.warning : theme.font.primary};
  `}
`;

const CommentTopline = styled.div`
  display: flex;
  flex-wrap: wrap-reverse;
  align-items: flex-end;
  gap: 6px 12px;
`;

const CommentFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 6px 10px;
  margin-top: 8px;
  && > select {
    margin-left: 0;
  }
  && > .br-comment-actions {
    margin: 0;
  }
`;

const CommentHeader = styled.div<{ $compact: boolean }>`
  flex: 1 1 180px;
  min-width: 0;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  ${({ $compact }) =>
    $compact &&
    css`
      flex-basis: 120px;
      > div {
        min-width: 0;
      }
      > div:first-child {
        flex-shrink: 0;
      }
      .author {
        white-space: normal !important;
        overflow-wrap: anywhere;
      }
    `}
`;

const CommentTextWithLineBreak = styled.div`
  margin-top: 5px;
  white-space: pre-line;
  hyphens: auto;
  word-break: break-word;
`;

const StyledComment = styled.article`
  :hover {
    z-index: 999;
    box-shadow: rgba(100, 100, 111, 0.2) 0px 7px 29px 0px;
  }
`;

type Props = {
  comment: any;
  enableFilters?: boolean;
  isActive: boolean;
  hasReplies: boolean;
  showChapterLinks?: boolean;
  onSizeChange?: (commentId: string, height: number) => void;
  /** When true (reader sidebar): hide "Inline comment" label; icon focuses comment in text instead of linking to chapter */
  inReaderView?: boolean;
  /** Called when user clicks the focus icon in reader view – focus this comment and scroll to it in the text */
  onFocusInContent?: (comment: any) => void;
};

const CommentElement: React.FC<Props> = ({
  comment,
  enableFilters,
  isActive,
  hasReplies,
  showChapterLinks,
  onSizeChange,
  inReaderView = false,
  onFocusInContent,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { t } = useTranslation();
  const {flags} = useFeatureFlags();
  const { width, height, ref } = useElementSize<HTMLDivElement>();
  const compactFeedback = width !== undefined && width <= 420;

  const currentBook = useSelector(state => state.book.currentBook);
  const currentSpineIndex = useSelector(
    state => state.readerApp.currentSpineIndex
  );
  const commentStatusOptions = useSelector(
    state => state.comment.commentStatusOptions
  );
  const user = useSelector(state => state.user.user);
  const userProfile = useSelector(state => state.user.userProfile);
  const canReport = !!(flags?.moderation_reporting ||
    (flags?.moderation_reporting_beta_access_allowed && userProfile?.betaFeatures?.moderation_reporting)) &&
    user.uid === currentBook?.author?._id;
  const [reportTarget, setReportTarget] = useState<FeedbackReportTarget | null>(null);

  const [replyOrigin, setReplyOrigin] = useState(getFeedbackPreference());
  const [replyText, setReplyText] = useState('');
  const [newReplyText, setNewReplyText] = useState('');
  const [newComment, setNewComment] = useState(
    comment?._id?.substr(0, 11) === 'placeHolder'
  );
  const [aiOrigin, setAiOrigin] = useState(getFeedbackPreference());
  const [newCommentText, setNewCommentText] = useState(comment?.text);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [showReplies, setShowReplies] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  /** Focus this comment if in url */
  useEffect(() => {
    const query = queryString.parse(location.search);
    if (comment._id === query.comment) {
      if (query.reply || query.crid) {
        // TODO: align these urls so that they use the same query parameter for comment and reply
        // a reply id is in the url, make sure that the replies are visible
        setShowReplies(true);
      }
      dispatch(
        setActiveInlineComment({ commentId: comment._id, show: 'sidebar' })
      );
    }
  }, []);

  /** Inform parent about size change */
  const debouncedOnSizeChange = useCallback(
    debounce((commentId: string, newHeight: number) => {
      onSizeChange && onSizeChange(commentId, newHeight);
    }, 100), // Adjust the debounce delay as needed
    []
  );
  useEffect(() => {
    if (height !== undefined) debouncedOnSizeChange(comment._id, height);
  }, [height]);

  const handleReplyTextUpdate = (event: any, data: any) => {
    setReplyText(data.value);
  };

  const handleNewCommentTextUpdate = (event: any, data: any) => {
    newComment && dispatch(editCommentPlaceHolderText(comment._id, data.value)); // only set comment text in redux on placeholder comment
    setNewCommentText(data.value);
  };

  const onSubmitReply = async () => {
    if (replyOrigin !== 'personal' || !(replyText?.length > 0)) {
      return;
    }
    const idToken = await authUtil.getFreshIdToken();
    dispatch(submitReply(idToken, comment, {text: replyText, aiOrigin: replyOrigin}, (saved: any) => {
      if (saved) {setShowReplyForm(false); setReplyText(''); setReplyOrigin('undeclared');}
    }));
  };

  const onSubmitComment = async (comment: any) => {
    setIsSubmitting(true);
    const idToken = await authUtil.getFreshIdToken();
    if (aiOrigin !== 'personal' || !(newCommentText?.length > 0) || !currentBook) {
      setIsSubmitting(false);
      return;
    }

    const currentChapter = currentBook.parts[currentSpineIndex];
    // if we have a chapter to comment on
    if (currentChapter !== undefined) {
      // submit the comment first
      dispatch(
        submitComment(
          idToken,
          currentBook._id,
          currentChapter._id,
          newCommentText,
          {
            aiOrigin,
            kind: 'inline',
            inlineInfo: comment.inlineInfo,
          },
          (commentId: any) => {
            setIsSubmitting(false);
            if (!commentId) return;
            setNewCommentText('');
            if (newComment) {
              dispatch(removeInlineCommentPlaceholder(comment._id));
              dispatch(setActiveInlineComment({ commentId: undefined, show: undefined }));
            }

            // make sure we blur the focus otherwise we loose the ability to show customToolBar
            if (document.activeElement instanceof HTMLElement && document.activeElement !== document.body) {
              document.activeElement.blur();
            }
          }
        )
      );
    } else { setIsSubmitting(false); }
  };

  const onDeleteComment = async (event: React.MouseEvent<HTMLButtonElement>) => {
    const commentId = event.currentTarget.dataset.commentId;
    const idToken = await getFreshIdToken();
    dispatch(deleteComment(idToken, commentId));
  };

  const onDeleteReply = async (event: React.MouseEvent<HTMLButtonElement>) => {
    const replyId = event.currentTarget.dataset.replyId;
    const idToken = await getFreshIdToken();
    dispatch(
      deleteReply(idToken, {
        bookId: comment.book._id,
        chapterId: comment.part._id,
        commentId: comment._id,
        replyId,
      })
    );
  };

  const toggleReplyForm = (event: any) => {
    preventDefaults(event);
    setShowReplyForm(!showReplyForm);
    setReplyOrigin(getFeedbackPreference());
    setReplyText('');
  };

  const toggleShowReplies = (event: any) => {
    preventDefaults(event);
    setShowReplies(!showReplies);
  };

  const handleCommentStatusUpdate = async (e: any, data: any) => {
    const idToken = await authUtil.getFreshIdToken();
    dispatch(
      updateComment(idToken, {
        bookId: comment.book._id,
        chapterId: comment.part._id,
        commentId: comment._id,
        status: data.value,
      })
    );
  };

  const handleCommentUpdate = async (comment: any) => {
    if (aiOrigin !== 'personal') return;
    const idToken = await authUtil.getFreshIdToken();
    dispatch(
      updateComment(idToken, {
        bookId: comment.book._id,
        chapterId: comment.part._id,
        commentId: comment._id,
        aiOrigin,
        text: newCommentText,
      }, (saved: any) => { if (saved) setNewCommentText(''); })
    );
  };

  const handleReplyUpdate = async (reply: any) => {
    if (replyOrigin !== 'personal') return;
    const idToken = await authUtil.getFreshIdToken();
    dispatch(
      updateReply(idToken, {
        bookId: comment.book._id,
        chapterId: comment.part._id,
        commentId: comment._id,
        replyId: reply._id,
        text: replyText,
        aiOrigin: replyOrigin,
      }, (saved: any) => {if (saved) {setReplyText(''); setShowReplyForm(false); setReplyOrigin('undeclared');}})
    );
  };

  const onEditCommentText = () => {
    dispatch(editComment({ commentId: comment._id, editing: true }));
    setAiOrigin('undeclared');
    setNewCommentText(comment.text);
  };

  const onCancelEditCommentText = () => {
    if (newComment) {
      dispatch(removeInlineCommentPlaceholder(comment._id));
    } else {
      dispatch(editComment({ commentId: comment._id, editing: false }));
    }
    setNewCommentText('');
    dispatch(setActiveInlineComment({ commentId: undefined, show: undefined }));
  };

  const onEditingReplyText = (event: React.MouseEvent<HTMLButtonElement>) => {
    const reply = comment.replies.find(
      (replyObject: any) => replyObject._id === event.currentTarget.dataset.replyId
    );
    if (reply) {
      dispatch(
        editReply({ commentId: comment._id, replyId: reply._id, editing: true })
      );
      setReplyText(reply.text);
      setReplyOrigin('undeclared');
      setShowReplyForm(false);
    }
  };

  const onCancelEditingReplyText = (e: any, data: any) => {
    if (newComment) {
      dispatch(removeInlineCommentPlaceholder());
    } else {
      dispatch(
        editReply({
          commentId: comment._id,
          replyId: data['data-reply-id'],
          editing: false,
        })
      );
    }
    setNewReplyText('');
  };

  const goToCommentInContent = (event: any) => {
    console.log('gotocomments incontnent');
    preventDefaults(event);
    if (!enableFilters) {
      return;
    }
    dispatch(setFilteredReaders(comment.user));
  };

  const preventDefaults = (event: any) => {
    event.stopPropagation();
    event.preventDefault();
  };

  if (comment.isLast && comment._id === undefined) {
    return null;
  }

  return (
    <div ref={ref}>
      <StyledComment
        className={isActive ? 'comment-element-active' : 'comment-element'}
      >
        <div className="br-comment-content">
          <CommentTopline>
            <CommentHeader $compact={compactFeedback}>
              <div style={{ width: 25, height: 25 }}>
                <Avatar
                  name={comment.user?.displayName ?? t('Unnamed')}
                  userId={comment.user?._id ?? null}
                  diameter={25}
                />
              </div>
              <div
                style={{ display: 'flex', flexDirection: 'column', flex: 1 }}
              >
                <div style={{ display: 'flex', flexDirection: 'row', flex: 1 }}>
                  <span className="br-comment-author"
                    onClick={goToCommentInContent}
                    role={enableFilters ? 'button' : undefined}
                    tabIndex={enableFilters ? 0 : undefined}
                    onKeyDown={event => {
                      if (enableFilters && (event.key === 'Enter' || event.key === ' ')) {
                        goToCommentInContent(event);
                      }
                    }}
                    style={{ marginLeft: 5, whiteSpace: 'pre', flex: 1 }}
                  >
                    {comment.user?.displayName ?? t('Unnamed')}
                  </span>
                  {((showChapterLinks &&
                    comment.part?._id &&
                    comment.contentVersion?.versionNumber !== undefined) ||
                    (inReaderView && onFocusInContent)) && (
                    <span className="br-comment-metadata"
                      key="title"
                      style={{ marginLeft: 'auto' }}
                    >
                      <span>
                        {!inReaderView &&
                          (comment.inlineInfo
                            ? t('InlineComment')
                            : t('ChapterComment'))}
                        {inReaderView && onFocusInContent ? (
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={e => {
                              e.preventDefault();
                              e.stopPropagation();
                              onFocusInContent(comment);
                            }}
                            onKeyDown={e => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                onFocusInContent(comment);
                              }
                            }}
                            style={{
                              marginLeft: inReaderView ? 0 : 6,
                              cursor: 'pointer',
                              color: '#4183c4',
                              display: 'inline-block',
                              verticalAlign: 'middle',
                            }}
                            title={t('ShowInText')}
                            aria-label={t('ShowInText')}
                          >
                            <AppIcon name="linkify" style={{ color: '#4183c4' }} />
                          </span>
                        ) : (
                          <Link
                            to={`/books/${comment.book._id}/content/v${comment.contentVersion.versionNumber}/${comment.part._id}?cid=${comment._id}&kind=${comment.kind}`}
                            style={{
                              marginLeft: 6,
                              cursor: 'pointer',
                              color: '#4183c4',
                              display: 'inline-block',
                              verticalAlign: 'middle',
                            }}
                            title={t('GoToChapter')}
                            aria-label={t('GoToChapter')}
                          >
                            <AppIcon name="linkify" style={{ color: '#4183c4' }} />
                          </Link>
                        )}
                      </span>
                      {!inReaderView && comment.part?.title !== undefined && (
                        <>
                          <br />
                          <span>
                            {comment.part.title}
                            &nbsp; (v{comment.contentVersion.versionNumber})
                          </span>
                        </>
                      )}
                    </span>
                  )}
                </div>
                <span className="br-comment-metadata"
                  style={{
                    fontWeight: 'normal',
                    fontSize: '0.9em',
                    color: 'var(--br-muted)',
                    marginLeft: 5,
                  }}
                >
                  {comment.contentUpdatedAt
                    ? moment(comment.contentUpdatedAt).format('llll')
                    : moment(comment.createdAt).format('llll')}
                </span>
              </div>
              {comment.warning && <span title={t(comment.warning.key)} aria-label={t(comment.warning.key)}>
                <AppIcon name="exclamation triangle" aria-hidden="true" />
              </span>}
            </CommentHeader>
          </CommentTopline>
          {!comment.editing && (
            <CommentTextWithLineBreak>
              {/* {comment.reaction && getReaction(comment.reaction)} */}
              {!newComment && comment.text}
            </CommentTextWithLineBreak>
          )}
          <CommentFooter>
            {!newComment && (
              <FeedbackMenu
                origin={comment.aiOrigin}
                label="Comment actions"
                direction="right"
              >
                {user.uid === comment.user?._id && !comment.reaction && (
                  <button type="button"
                    data-comment-id={comment._id}
                    onClick={onEditCommentText}
                  >{t('Edit')}</button>
                )}
                {user.uid === comment.user?._id && (
                  <button type="button"
                    data-comment-id={comment._id}
                    onClick={onDeleteComment}
                  >{t('Delete')}</button>
                )}
                <ReportAiDisclosure
                  menuItem
                  bookId={currentBook._id}
                  feedbackId={comment._id}
                />
                {canReport && comment.user?._id && comment.user._id !== user.uid && (
                  <button type="button"
                    onClick={() => setReportTarget({
                      kind: 'comment', contentId: comment._id,
                      userId: comment.user._id, displayName: comment.user.displayName || t('Unnamed')
                    })}
                  >{t('ReportOrBlock')}</button>
                )}
              </FeedbackMenu>
            )}
            <div className="br-comment-actions" style={{ cursor: 'pointer' }}>
              {comment.reaction && <span className="br-comment-action">{getReaction(comment.reaction)}</span>}
              {!showReplies &&
                !newComment &&
                !comment.editing &&
                accessUtil.isAllowed(currentBook.access, [
                  'edit',
                  'comment',
                ]) && (
                  <button type="button" className="br-comment-action"
                    data-comment-id={comment._id}
                    onClick={toggleReplyForm}
                  >
                    {t('EnterReply')}
                  </button>
                )}
              {hasReplies && [
                !showReplies && (
                  <button type="button" className="br-comment-action"
                    key="show-replies-action"
                    data-comment-id={comment._id}
                    onClick={toggleShowReplies}
                  >
                    {t('ShowXReplies', { count: comment.replies.length })}
                  </button>
                ),
                showReplies && (
                  <button type="button" className="br-comment-action"
                    key="hide-replies-action"
                    data-comment-id={comment._id}
                    onClick={toggleShowReplies}
                  >
                    {t('HideXReplies', { count: comment.replies.length })}
                  </button>
                ),
              ]}
              {user.uid === currentBook.author._id && <label className="br-comment-status">
                <span>{t('Status')}</span>
                <select value={comment.status || ''}
                  onChange={event => handleCommentStatusUpdate(event, {value: event.currentTarget.value})}>
                  <option value="" disabled>{t('Status')}</option>
                  {commentStatusOptions.map(option => <option key={option.key} value={option.value}>{option.text}</option>)}
                </select>
              </label>}
            </div>
          </CommentFooter>
        </div>
        {(newComment || comment.editing) && ( // form for inputing new comments
          <div className="br-comment-form">
            <FeedbackOrigin value={aiOrigin} onChange={setAiOrigin} />
            <TextAreaWithSave
              aria-label={t('LeaveComment')}
              data-comment-id={comment._id}
              onClick={(e: any) => e.stopPropagation()}
              value={comment.editing ? newCommentText : comment.text} // show the local state text only on update edits
              // value={newCommentText} // show the local state text only on update edits
              onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => handleNewCommentTextUpdate(event, {value: event.currentTarget.value})}
              onSave={() => {
                if (newComment) {
                  onSubmitComment(comment);
                } else {
                  handleCommentUpdate(comment);
                }
              }}
              autoFocus
            />
            <div className="br-comment-form-layout">
              <WordCount warning={newCommentText?.length > 2000}>
                {Number(newCommentText?.length ?? 0).toLocaleString()} /{' '}
                {Number(2000).toLocaleString()}
              </WordCount>
              <div className="br-comment-form-actions">
                <Button
                  data-comment-id={comment._id}
                  disabled={
                    aiOrigin !== 'personal' ||
                    !newCommentText ||
                    newCommentText?.length === 0 ||
                    newCommentText?.length > 2000
                  }
                  onClick={e => {
                    preventDefaults(e);
                    if (newComment) {
                      onSubmitComment(comment);
                    } else {
                      handleCommentUpdate(comment);
                    }
                  }}
                  aria-busy={isSubmitting}
                  size="small"
                >{t('LeaveComment')}</Button>
                <Button
                  data-comment-id={comment._id}
                  onClick={e => {
                    preventDefaults(e);
                    onCancelEditCommentText();
                  }}
                  disabled={isSubmitting}
                  variant="secondary"
                  size="small"
                >{t('Cancel')}</Button>
              </div>
            </div>
          </div>
        )}
        {hasReplies && showReplies && (
          <div className="br-comment-replies" style={{ padding: '1em 0 1em 0em' }}>
            {comment.replies.map((reply: any, index: any, array: any) => (
              <article className="br-comment-reply" key={reply._id}>
                <CommentTopline>
                  <CommentHeader $compact={compactFeedback}>
                    <div style={{ width: 22, height: 22 }}>
                      <Avatar
                        name={
                          reply.user.displayName &&
                          reply.user.displayName !== ''
                            ? reply.user.displayName
                            : t('Unnamed')
                        }
                        userId={reply.user._id}
                        diameter={22}
                      />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <div style={{ display: 'flex', flexDirection: 'row' }}>
                        <span className="br-comment-author"

                          style={{ marginLeft: 5, whiteSpace: 'pre' }}
                        >
                          {reply.user.displayName &&
                          reply.user.displayName !== ''
                            ? reply.user.displayName
                            : t('Unnamed')}
                        </span>
                      </div>
                      <span className="br-comment-metadata"
                        style={{
                          fontWeight: 'normal',
                          fontSize: '0.9em',
                          opacity: 0.5,
                          marginLeft: 5,
                        }}
                      >
                        {reply.updatedAt !== reply.createdAt
                          ? moment(reply.updatedAt).format('llll')
                          : moment(reply.createdAt).format('llll')}
                      </span>
                    </div>
                    {reply.reaction && !reply.editing && (
                      <span className="br-comment-metadata">
                        {getReaction(reply.reaction)}
                      </span>
                    )}
                  </CommentHeader>
                </CommentTopline>
                <div className="br-comment-content">
                  <CommentTextWithLineBreak>
                    {!reply.editing && reply.text}
                  </CommentTextWithLineBreak>
                  <CommentFooter>
                    {reply._id ? (
                      <FeedbackMenu
                        origin={reply.aiOrigin}
                        label="Reply actions"
                        direction="right"
                      >
                        {user.uid === reply.user._id && !reply.reaction && (
                          <button type="button"
                            data-reply-id={reply._id}
                            data-comment-id={comment._id}
                            onClick={onEditingReplyText}
                          >{t('Edit')}</button>
                        )}
                        {user.uid === reply.user._id && (
                          <button type="button"
                            data-reply-id={reply._id}
                            data-comment-id={comment._id}
                            onClick={onDeleteReply}
                          >{t('Delete')}</button>
                        )}
                        <ReportAiDisclosure
                          menuItem
                          bookId={currentBook._id}
                          feedbackId={reply._id}
                        />
                        {canReport && reply.user?._id && reply.user._id !== user.uid && (
                          <button type="button"
                            onClick={() => setReportTarget({
                              kind: 'reply', contentId: reply._id,
                              userId: reply.user._id, displayName: reply.user.displayName || t('Unnamed')
                            })}
                          >{t('ReportOrBlock')}</button>
                        )}
                      </FeedbackMenu>
                    ) : (
                      <FeedbackBadge origin={reply.aiOrigin} />
                    )}
                    {index === array.length - 1 &&
                      !reply.editing &&
                      accessUtil.isAllowed(currentBook.access, [
                        'edit',
                        'comment',
                      ]) && (
                        <div className="br-comment-actions">
                          <button type="button" className="br-comment-action"
                            data-comment-id={comment._id}
                            onClick={toggleReplyForm}
                          >
                            {t('EnterReply')}
                          </button>
                        </div>
                      )}
                  </CommentFooter>
                </div>
                {reply.editing && ( // inline replyform
                  <div className="br-comment-form">
                    <FeedbackOrigin
                      value={replyOrigin}
                      onChange={setReplyOrigin}
                    />
                    <TextAreaWithSave
                      aria-label={t('EnterReply')}
                      onClick={(e: any) => e.stopPropagation()}
                      value={replyText}
                      data-comment-id={comment._id}
                      data-reply-id={reply._id}
                      onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => handleReplyTextUpdate(event, {value: event.currentTarget.value})}
                      onSave={() => {
                        if (reply.editing) {
                          handleReplyUpdate(reply);
                        } else {
                          onSubmitReply();
                        }
                      }}
                    />
                    <div className="br-comment-form-layout">
                      <div className="br-comment-form-actions">
                        <Button
                          data-comment-id={comment._id}
                          data-reply-id={reply._id}
                          disabled={
                            replyOrigin !== 'personal' ||
                            !replyText ||
                            replyText.length === 0
                          }
                          onClick={e => {
                            preventDefaults(e);
                            if (reply.editing) {
                              handleReplyUpdate(reply);
                            } else {
                              onSubmitReply();
                            }
                          }}
                          aria-busy={isSubmitting}
                          size="small"
                        >{t('EnterReply')}</Button>
                        <Button
                          data-comment-id={comment._id}
                          data-reply-id={reply._id}
                          onClick={e => {
                            preventDefaults(e);
                            onCancelEditingReplyText(e, {'data-reply-id': reply._id});
                          }}
                          disabled={isSubmitting}
                          variant="secondary"
                          size="small"
                        >{t('Cancel')}</Button>
                      </div>
                    </div>
                  </div>
                )}
              </article>
            ))}
          </div>
        )}
        {
          // form for adding new replies
          showReplyForm && !newComment && (
            <div className="br-comment-form">
              <FeedbackOrigin value={replyOrigin} onChange={setReplyOrigin} />
              <TextAreaWithSave
                aria-label={t('EnterReply')}
                data-comment-id={comment._id}
                onClick={(e: any) => e.stopPropagation()}
                value={replyText}
                onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => handleReplyTextUpdate(event, {value: event.currentTarget.value})}
                onSave={() => {
                  onSubmitReply();
                }}
              />
              <div className="br-comment-form-layout">
                <div className="br-comment-form-actions">
                  <Button
                    data-comment-id={comment._id}
                    disabled={
                      replyOrigin !== 'personal' ||
                      !replyText ||
                      replyText.length === 0
                    }
                    onClick={e => {
                      preventDefaults(e);
                      onSubmitReply();
                    }}
                    aria-busy={isSubmitting}
                    size="small"
                  >{t('EnterReply')}</Button>
                  <Button
                    data-comment-id={comment._id}
                    onClick={e => {
                      preventDefaults(e);
                      toggleReplyForm(e);
                    }}
                    disabled={isSubmitting}
                    variant="secondary"
                    size="small"
                  >{t('Cancel')}</Button>
                </div>
              </div>
            </div>
          )
        }
        {comment.copyOf && (
          <span style={{ opacity: 0.8, fontSize: '0.8em', color: 'grey' }}>
            <em>{t('CommentsCopiedFromOriginal')}</em>
          </span>
        )}
      </StyledComment>
      <FeedbackReportModal bookId={currentBook?._id} target={reportTarget} onClose={() => setReportTarget(null)} />
    </div>
  );
};
export default CommentElement;
